import React, { useState, useEffect } from 'react';
import { useAtlasStore, AtlasState } from '../../../../store/atlasStore';
import { SparkFlow } from '../../types/sparkTypes';
import styles from '../../styles/Modal.module.css';

interface FlowSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (flowFqn: string, projectName: string) => Promise<string | null>;
}

export const FlowSelectionModal: React.FC<FlowSelectionModalProps> = ({ 
  isOpen, 
  onClose, 
  onCreate 
}) => {
  const fetchSparkFlows = useAtlasStore((state: AtlasState) => state.fetchSparkFlows);
  const [flows, setFlows] = useState<SparkFlow[]>([]);
  const [selectedFlow, setSelectedFlow] = useState<string>('');
  const [projectName, setProjectName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setSparkUserInteractionBlockingRefresh = useAtlasStore((state: AtlasState) => state.setSparkUserInteractionBlockingRefresh);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setError(null);
      setProjectName('');
      setSelectedFlow('');
      fetchSparkFlows()
        .then((fetchedFlows: SparkFlow[] | undefined) => {
          const availableFlows = fetchedFlows || [];
          setFlows(availableFlows);
          // Auto-select if there's only one flow
          if (availableFlows.length === 1) {
            setSelectedFlow(availableFlows[0].fqn);
          }
        })
        .catch((err: any) => {
            console.error("Failed to fetch flows:", err);
            setError("Failed to load available types.");
        })
        .finally(() => setIsLoading(false));
    }
  }, [isOpen, fetchSparkFlows]);

  // Effect to block/unblock background refresh when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSparkUserInteractionBlockingRefresh(true);
      // Cleanup function to unblock when modal closes or component unmounts
      return () => {
        setSparkUserInteractionBlockingRefresh(false);
      };
    }
    // No cleanup needed if modal wasn't open
  }, [isOpen, setSparkUserInteractionBlockingRefresh]);

  const handleCreate = async () => {
    if (!selectedFlow) {
        setError("Please select a type.");
        return;
    }
    if (!projectName.trim()) {
        setError("Please enter a project name.");
        return;
    }
    setIsLoading(true);
    setError(null);
    const newProjectFqn = await onCreate(selectedFlow, projectName.trim());
    setIsLoading(false);
    if (newProjectFqn) {
        onClose();
    }
  };

  const isSubmitDisabled = isLoading || !selectedFlow || !projectName.trim() || flows.length === 0;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      if (!isSubmitDisabled) {
        handleCreate();
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <h2>New Project</h2>
        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.formGroup}>
            <label htmlFor="project-name">Name</label>
            <input
                type="text"
                id="project-name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                className={styles.textInput}
                placeholder="Enter project name..."
            />
        </div>

        {isLoading && !flows.length ? (
          <p>Loading types...</p>
        ) : (
          <div className={styles.formGroup}>
            <label htmlFor="flow-select">Type</label>
            <select 
                id="flow-select"
                value={selectedFlow}
                onChange={(e) => setSelectedFlow(e.target.value)}
                disabled={isLoading}
                className={styles.selectInput}
            >
                <option value="" disabled>-- Select a type --</option>
                {flows.map(flow => (
                <option key={flow.fqn} value={flow.fqn}>
                    {flow.name || flow.fqn}
                </option>
                ))}
            </select>
          </div>
        )}

        <div className={styles.modalActions}>
        <button
            onClick={handleCreate}
            disabled={isSubmitDisabled}
            className={styles.buttonPrimary}
          >
            {isLoading ? 'Creating...' : 'Create Project'}
          </button>
          <button onClick={onClose} disabled={isLoading} className={styles.buttonSecondary}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}; 